import React, { type FC } from 'react';

import { ModalTransition } from '@atlaskit/modal-dialog';
import { Box } from '@atlaskit/primitives/compiled';

import { InspectPermissionsDialog } from '@confluence/inspect-permissions/entry-points/InspectPermissionsDialog.ts';
import {
	AccessDetailsDialog as SpaceAccessDetailsDialog,
	type AccessDetailsDialogProps,
} from '@confluence/space-access-details/entry-points/AccessDetailsDialog.ts';

import {
	ShareAndRestrictDialog,
	type ShareAndRestrictDialogProps,
} from './shareAndRestrictDialog/ShareAndRestrictDialog.tsx';
import {
	CloseConfirmationDialog,
	type CloseConfirmationDialogProps,
} from './CloseConfirmationDialog.tsx';
import {
	GroupNotifyWarningDialog,
	type GroupNotifyWarningDialogProps,
} from './GroupNotifyWarningDialog.tsx';
import {
	NotifyLargeAudienceWarningDialog,
	type NotifyLargeAudienceWarningDialogProps,
} from './NotifyLargeAudienceWarningDialog.tsx';
import type { InspectPermsDialogProps } from './shareAndRestrictDialog/header/OverflowMenu.tsx';
import {
	RestrictedAccessWarningDialog,
	type RestrictedAccessWarningDialogProps,
} from './resolveRestrictions/RestrictedAccessWarningDialog.tsx';
import {
	ResolveRestrictionsDialog,
	type ResolveRestrictionsDialogProps,
} from './resolveRestrictions/ResolveRestrictionsDialog.tsx';
import {
	OpenAccessWarningDialog,
	type OpenAccessWarningDialogProps,
} from './openAccessWarning/OpenAccessWarningDialog.tsx';
import {
	RestrictAccessConfirmationDialog,
	type RestrictAccessConfirmationDialogProps,
} from './restrictAccess/RestrictAccessConfirmationDialog.tsx';

export type DialogsProps = {
	shareAndRestrictDialog?: ShareAndRestrictDialogProps;
	restrictedAccessWarningDialog?: RestrictedAccessWarningDialogProps;
	resolveRestrictionsDialog?: ResolveRestrictionsDialogProps;
	groupNotifyWarningDialog?: GroupNotifyWarningDialogProps;
	notifyLargeAudienceWarningDialog?: NotifyLargeAudienceWarningDialogProps;
	closeConfirmationDialog?: CloseConfirmationDialogProps;
	inspectPermsDialog?: InspectPermsDialogProps;
	spaceAccessDetails?: AccessDetailsDialogProps;
	openAccessWarningDialog?: OpenAccessWarningDialogProps;
	restrictAccessConfirmationDialog?: RestrictAccessConfirmationDialogProps;
	// Props for other dialog possibilities would go here
};

/**
 * A common mounting host for all the top-level dialogs the USD might have
 */
export const Dialogs: FC<DialogsProps> = ({
	shareAndRestrictDialog,
	restrictedAccessWarningDialog,
	resolveRestrictionsDialog,
	groupNotifyWarningDialog,
	notifyLargeAudienceWarningDialog,
	closeConfirmationDialog,
	spaceAccessDetails,
	inspectPermsDialog,
	openAccessWarningDialog,
	restrictAccessConfirmationDialog,
}: DialogsProps) => (
	<Box>
		<ModalTransition>
			{shareAndRestrictDialog && <ShareAndRestrictDialog {...shareAndRestrictDialog} />}
		</ModalTransition>
		<ModalTransition>
			{groupNotifyWarningDialog && <GroupNotifyWarningDialog {...groupNotifyWarningDialog} />}
		</ModalTransition>
		<ModalTransition>
			{notifyLargeAudienceWarningDialog && (
				<NotifyLargeAudienceWarningDialog {...notifyLargeAudienceWarningDialog} />
			)}
		</ModalTransition>
		<ModalTransition>
			{closeConfirmationDialog && <CloseConfirmationDialog {...closeConfirmationDialog} />}
		</ModalTransition>
		<ModalTransition>
			{spaceAccessDetails && <SpaceAccessDetailsDialog {...spaceAccessDetails} />}
		</ModalTransition>
		{/* Using ModalTransition here is preventing InspectPermissionsDialog from re-rendering after first time */}
		{inspectPermsDialog && <InspectPermissionsDialog {...inspectPermsDialog} />}
		<ModalTransition>
			{restrictedAccessWarningDialog && (
				<RestrictedAccessWarningDialog {...restrictedAccessWarningDialog} />
			)}
		</ModalTransition>
		<ModalTransition>
			{resolveRestrictionsDialog && <ResolveRestrictionsDialog {...resolveRestrictionsDialog} />}
		</ModalTransition>
		<ModalTransition>
			{openAccessWarningDialog && <OpenAccessWarningDialog {...openAccessWarningDialog} />}
		</ModalTransition>
		<ModalTransition>
			{restrictAccessConfirmationDialog && (
				<RestrictAccessConfirmationDialog {...restrictAccessConfirmationDialog} />
			)}
		</ModalTransition>
	</Box>
);
